import type { Command } from '../types/command';

/**
 * Parsed shape of a palette input value with respect to snap syntax. The
 * prefix is configurable -- `@`, `>`, `in:`, etc. are all valid; we only
 * require the input to *start* with it.
 *
 * - `none`: query is plain text (doesn't start with prefix). No special
 *   handling.
 * - `prefix`: input is `${prefix}<text>` with no whitespace separator yet.
 *   `text` may be empty (just the bare prefix) -- caller should list every
 *   available snap as suggestions.
 * - `commit`: input is `${prefix}<snap><whitespace><rest>` where `<snap>`
 *   is a known key. Caller should set the matched command as the entered
 *   root with `rest` as the initial in-mode query.
 *
 * Only the *leading* occurrence of the prefix matters. A query like
 * `jane@example.com` (with prefix `@`) is `none` because the `@` isn't at
 * position 0.
 */
export type SnapInputParse =
  | { kind: 'none' }
  | { kind: 'prefix'; prefix: string }
  | { kind: 'commit'; command: Command; rest: string };

/**
 * Collect every command that has opted in to snap support into a
 * `Map<snapKey, command>`.
 *
 * Snap is purely opt-in: a command is a snap target iff its `snap`
 * field is set to a non-empty string. There's no `name`-based
 * fallback (the previous incarnation auto-snapped every scope to
 * `name.toLowerCase()`; that was free discoverability with hidden
 * coupling -- renaming a scope silently broke its snap binding).
 *
 * On collision, later entries win: callers pass commands in the
 * order they want resolved (the registry walker passes them in
 * registration order, so most-recently-registered wins).
 */
export function buildSnapRegistry(commands: Iterable<Command>): Map<string, Command> {
  const result = new Map<string, Command>();
  for (const command of commands) {
    if (command.snap !== undefined && command.snap.length > 0) {
      result.set(command.snap, command);
    }
  }
  return result;
}

/**
 * Parse a palette input value against the registered snaps using a
 * configurable prefix. Pure function; used by the autocomplete
 * component to switch the listbox source to snap suggestions, and by
 * the input-change handler to detect space-triggered commits.
 */
export function parseSnapInput(input: string, snapPrefix: string, snaps: Map<string, Command>): SnapInputParse {
  if (snapPrefix.length === 0 || !input.startsWith(snapPrefix)) {
    return { kind: 'none' };
  }
  const afterPrefix = input.slice(snapPrefix.length);
  // First whitespace separates the snap key from the would-be query.
  const sepMatch = /\s/.exec(afterPrefix);
  if (sepMatch === null) {
    // Still in prefix mode -- the user is typing the snap name.
    return { kind: 'prefix', prefix: afterPrefix };
  }
  const key = afterPrefix.slice(0, sepMatch.index);
  const afterSep = afterPrefix.slice(sepMatch.index + 1);
  const command = snaps.get(key);
  if (command === undefined) {
    // `@unknown ` -- stay in prefix mode rather than silently stranding
    // the user. The trailing whitespace stays in the input value
    // (we don't mutate input here); the listbox shows "no matching snap".
    return { kind: 'prefix', prefix: key };
  }
  return { kind: 'commit', command, rest: afterSep };
}
